/**
 * Saved chart templates — one chart, kept to apply to another.
 *
 * `api/presets.ts`'s split, for its reason: **reads degrade, writes throw.** A
 * listing that fails leaves a picker with fewer rows, which is chrome; a save
 * that failed silently loses a chart somebody arranged, and they find out when
 * they go looking for it.
 *
 * ## What this module does not do
 *
 * It does not apply. Applying writes the definition into a `ChartGroup` through
 * the ordinary workspace dispatch, so it is an edit the user can undo like any
 * other — and two people applying one template must not end up holding the same
 * `ChartGroup.id`, which the workspace tree is addressed by. `lib/chart-template.ts`
 * owns the copy; this owns the wire.
 */

import type { ChartGroup } from "@qufolio/model";

import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL);

/** Whether there is a server to ask. Off, the menu items are not offered. */
export const chartTemplatesConfigured: boolean = BASE !== "";

const ROOT = `${BASE}/chart-templates`;

/**
 * One template as the picker lists it — no chart until one is chosen.
 *
 * `paneCount` and `symbol` are columns on the server rather than something
 * parsed out of the definition, which is what lets a list of a dozen cost one
 * small response instead of a dozen charts.
 */
export interface ChartTemplateSummary {
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  /**
   * Who owns it. `user` is the caller's own — the common case here, unlike
   * screens and layouts, where a published one is the usual kind.
   */
  ownerKind: "system" | "org" | "user";
  ownerKey: string | null;
  /**
   * A group's display name, on a group's template only — what the dialog tags
   * the row with (*from JP*), since `ownerKey` is `grp-jp`. Absent from an
   * older server, when the key stands in.
   */
  ownerName?: string;
  /** A group's published template: its current version. */
  version?: number;
  paneCount: number;
  /** Absent when the template was saved without one. Never the empty string. */
  symbol: string | null;
}

/** The whole thing, fetched when one is applied. */
export interface ChartTemplate extends ChartTemplateSummary {
  /**
   * A `ChartGroup` minus its id, its window, its annotations and the PIP.
   *
   * Partial rather than the full type on purpose: what comes back is
   * deliberately *less* than a chart, and typing it as a whole one would let a
   * caller read `id` off it and get `undefined` at runtime.
   */
  definition: Partial<ChartGroup>;
}

export interface NewChartTemplate {
  name: string;
  description?: string;
  category?: string;
  definition: Partial<ChartGroup>;
  /**
   * Whether the symbol travels.
   *
   * **Decided at save time, not at apply time**, because the person saving
   * knows whether the symbol is the point and the person applying does not: a
   * club's "Breadth" chart is about `SPY` and is nonsense on anything else,
   * where "three indicators and a volume pane" applies to whatever is on
   * screen. Omitted means true — the server's default, and the honest one for
   * a definition that arrived carrying a symbol.
   */
  keepSymbol?: boolean;
}

async function write<T>(url: string, init: RequestInit): Promise<T> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(url, {
    ...init,
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
  });
  if (!response.ok) {
    // `journal.ts`'s rule: the server answers a broken rule with a sentence —
    // `You already have a template called "Momentum".` — worth showing verbatim
    // rather than as the JSON it arrives in. Anything else says which call failed.
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail ?? `${init.method} failed (${response.status})`);
  }
  return (await response.json()) as T;
}

/** Every template this account may apply. Degrades to none. */
export async function fetchChartTemplates(): Promise<ChartTemplateSummary[]> {
  if (!BASE) return [];
  try {
    const response = await fetch(ROOT, { headers: await requestHeaders() });
    if (!response.ok) return [];
    const body = (await response.json()) as ChartTemplateSummary[];
    return Array.isArray(body) ? body : [];
  } catch {
    return [];
  }
}

/**
 * One template, whole.
 *
 * Null rather than a throw, and the caller leaves the chart alone: 404 covers
 * "deleted" and "no longer yours" alike, and the chart already on screen is the
 * last good one. `applyScreenTo` makes the same call for the same reason.
 */
export async function fetchChartTemplate(key: string): Promise<ChartTemplate | null> {
  if (!BASE) return null;
  try {
    const response = await fetch(`${ROOT}/${encodeURIComponent(key)}`, {
      headers: await requestHeaders(),
    });
    if (!response.ok) return null;
    return (await response.json()) as ChartTemplate;
  } catch {
    return null;
  }
}

/** Save one of the caller's own. The server mints the key and returns it. */
export async function createChartTemplate(
  input: NewChartTemplate,
): Promise<ChartTemplateSummary> {
  return write<ChartTemplateSummary>(ROOT, { method: "POST", body: JSON.stringify(input) });
}

/** Replace one of the caller's own, whole. */
export async function saveChartTemplate(
  key: string,
  input: NewChartTemplate,
): Promise<ChartTemplateSummary> {
  return write<ChartTemplateSummary>(`${ROOT}/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

/** Delete one of the caller's own. A club's answers 404, which throws here. */
export async function deleteChartTemplate(key: string): Promise<void> {
  await write<{ deleted: number }>(`${ROOT}/${encodeURIComponent(key)}`, { method: "DELETE" });
}

/**
 * Publish one to a club. Admin only — the server refuses otherwise.
 *
 * The key is derived from the name for `publishPreset`'s reason: an admin
 * naming a template "Breadth" should not also have to invent a slug, and the
 * collision they would otherwise cause is exactly what the unique constraint
 * refuses — so deriving it makes the two agree instead of failing later.
 */
export async function publishChartTemplate(input: {
  name: string;
  description?: string;
  ownerKey: string;
  definition: Partial<ChartGroup>;
  keepSymbol?: boolean;
}): Promise<ChartTemplateSummary> {
  const key = `${input.ownerKey}.${slug(input.name)}`;
  return write<ChartTemplateSummary>(
    `${BASE}/author/chart-templates/${encodeURIComponent(key)}`,
    {
      method: "PUT",
      body: JSON.stringify({
        name: input.name,
        description: input.description ?? "",
        ownerKind: "org",
        ownerKey: input.ownerKey,
        definition: input.definition,
        keepSymbol: input.keepSymbol ?? true,
      }),
    },
  );
}

/** A name to a key segment. `api/presets.ts`'s, kept identical deliberately. */
export function slug(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "template"
  );
}
